import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const DEFAULT_DATA_API_URL = 'https://dororaid-data.kukla1255.workers.dev';
const DATA_API_URL = String(import.meta.env?.VITE_DORORAID_DATA_API_URL || DEFAULT_DATA_API_URL).trim().replace(/\/+$/, '');
const PACK_ID = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const DATA_ID = /^[0-9]{1,24}$/;
const IMPORT_BATCH_SIZE = 8;

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

async function currentAccessToken() {
  const { data, error } = await requireClient().auth.getSession();
  if (error) throw error;
  const token = String(data?.session?.access_token || '').trim();
  if (!token) throw new Error('로그인 세션을 확인할 수 없습니다.');
  return token;
}

// 디시는 dcimg1~9 로 나눠 준다. 서버(stickerService.js)와 같은 기준이다.
const DCINSIDE_IMAGE_HOST = /^dcimg[0-9]+\.dcinside\.com$/;

export function isDcinsideSource(value) {
  try { return DCINSIDE_IMAGE_HOST.test(new URL(value).hostname); } catch { return false; }
}

function sourceUrl(value) {
  const raw = String(value || '').trim();
  let url;
  try { url = new URL(raw.startsWith('//') ? `https:${raw}` : raw); }
  catch { throw new Error('콘 원본 주소 형식이 올바르지 않습니다.'); }
  const allowedHost = url.hostname === 'ac.arca.live' || DCINSIDE_IMAGE_HOST.test(url.hostname);
  if (url.protocol !== 'https:' || !allowedHost || url.port || url.username || url.password || url.hash) {
    throw new Error('아카콘(ac.arca.live) · 디시콘(dcimg*.dcinside.com) HTTPS 원본만 가져올 수 있습니다.');
  }
  return url.href;
}

export function parseStickerCandidates(html) {
  if (typeof DOMParser === 'undefined') throw new Error('이 브라우저에서는 HTML 분석을 사용할 수 없습니다.');
  if (typeof html !== 'string' || !html.trim()) throw new Error('콘 HTML을 붙여넣어 주세요.');
  const document = new DOMParser().parseFromString(html, 'text/html');
  const entries = new Map();
  const nodes = [...document.querySelectorAll('img.emoticon, video.emoticon')];
  for (const node of nodes) {
    const dataId = String(node.getAttribute('data-id') || '').trim();
    if (!DATA_ID.test(dataId)) throw new Error('data-id가 없는 콘이 포함되어 있습니다.');
    const mediaType = node.tagName.toLowerCase() === 'video' ? 'video' : 'image';
    const next = {
      dataId,
      mediaType,
      sourceUrl: sourceUrl(node.getAttribute(mediaType === 'video' ? 'data-src' : 'src')),
      posterUrl: mediaType === 'video' && node.getAttribute('poster') ? sourceUrl(node.getAttribute('poster')) : null,
      displayOrder: entries.size,
    };
    const previous = entries.get(dataId);
    if (previous && (previous.mediaType !== next.mediaType || previous.sourceUrl !== next.sourceUrl || previous.posterUrl !== next.posterUrl)) {
      throw new Error(`같은 data-id(${dataId})에 서로 다른 원본이 있습니다.`);
    }
    if (!previous) entries.set(dataId, next);
    if (entries.size > 500) throw new Error('콘은 한 팩에 최대 500개까지 관리할 수 있습니다.');
  }
  // 디시콘 목록은 <ul class="dccon_list"><li><span class="img_dccon"><img src alt title> 모양이다.
  // data-id 가 없고 alt 도 숫자가 아닐 때가 있어서(2026-10-19 실제로 막혔다) 목록 순서로 번호를
  // 매긴다 - dataId 는 팩 안에서만 유일하면 된다. 같은 원본이 두 번 나오면 한 번만 센다.
  const dcSources = new Set();
  for (const node of document.querySelectorAll('.dccon_list img, .img_dccon img')) {
    const next = { dataId: '', mediaType: 'image', sourceUrl: sourceUrl(node.getAttribute('src') || node.getAttribute('data-src')), posterUrl: null, displayOrder: entries.size };
    if (dcSources.has(next.sourceUrl)) continue;
    dcSources.add(next.sourceUrl);
    next.dataId = String(dcSources.size);
    if (entries.has(next.dataId)) throw new Error(`같은 번호(${next.dataId})의 아카콘과 디시콘이 섞여 있습니다. 한 종류씩 가져와 주세요.`);
    entries.set(next.dataId, next);
    if (entries.size > 500) throw new Error('콘은 한 팩에 최대 500개까지 관리할 수 있습니다.');
  }
  if (!entries.size) throw new Error('분석할 수 있는 콘을 찾지 못했습니다.');
  return [...entries.values()];
}

async function request(path, { method = 'GET', body = null, allowPartial = false } = {}) {
  const token = await currentAccessToken();
  const response = await fetch(`${DATA_API_URL}${path}`, {
    method,
    cache: 'no-store',
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${token}`,
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let payload = null;
  try { payload = await response.json(); } catch {}
  if (!response.ok || (!allowPartial && payload?.ok !== true)) {
    const code = String(payload?.error || `HTTP ${response.status}`);
    throw new Error(`콘 관리 요청에 실패했습니다. (${code})`);
  }
  return payload || { ok: false };
}

export function loadAdminStickerPacks() {
  return request('/v1/admin/sticker-packs');
}

export function loadAdminStickerPack(packId) {
  if (!PACK_ID.test(packId)) throw new Error('올바른 콘팩 ID가 아닙니다.');
  return request(`/v1/admin/sticker-packs/${encodeURIComponent(packId)}`);
}

function htmlAttribute(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function compatibilityHtml(entries) {
  return entries.map((entry) => {
    const id = htmlAttribute(entry.dataId);
    if (entry.mediaType === 'video') {
      const poster = entry.posterUrl ? ` poster="${htmlAttribute(entry.posterUrl)}"` : '';
      return `<video class="emoticon" data-id="${id}" data-src="${htmlAttribute(entry.sourceUrl)}"${poster}></video>`;
    }
    return `<img class="emoticon" data-id="${id}" src="${htmlAttribute(entry.sourceUrl)}">`;
  }).join('');
}

export async function importStickerEntries({ packId, name, entries }) {
  if (!PACK_ID.test(packId)) throw new Error('콘팩 ID는 영문 소문자, 숫자, -, _만 사용할 수 있습니다.');
  if (!String(name || '').trim()) throw new Error('콘팩 이름을 입력해 주세요.');
  if (!Array.isArray(entries) || !entries.length || entries.length > IMPORT_BATCH_SIZE) {
    throw new Error(`한 번에 최대 ${IMPORT_BATCH_SIZE}개까지 가져올 수 있습니다.`);
  }

  const normalizedName = String(name).trim();
  const html = compatibilityHtml(entries);
  const first = await request('/v1/admin/stickers/import', {
    method: 'POST',
    allowPartial: true,
    body: { packId, name: normalizedName, entries, html },
  });

  // PR #328 Workers page through the compatibility HTML five at a time.
  // Follow their nextOffset until complete so an older Worker cannot drop the
  // tail of an 8-item client batch during a rolling deployment.
  if (!Number.isInteger(first?.nextOffset)) return first;

  const results = [...(first.results || [])];
  let nextOffset = first.nextOffset;
  let ok = first.ok !== false;
  while (Number.isInteger(nextOffset) && nextOffset < entries.length) {
    const remaining = entries.slice(nextOffset);
    const next = await request('/v1/admin/stickers/import', {
      method: 'POST',
      allowPartial: true,
      body: {
        packId,
        name: normalizedName,
        entries: remaining,
        html,
        offset: nextOffset,
      },
    });
    results.push(...(next.results || []));
    ok = ok && next.ok !== false;
    nextOffset = next.nextOffset;
  }
  return { ...first, ok, results, nextOffset: null };
}

export function saveStickerPackSettings(packId, updates) {
  if (!PACK_ID.test(packId)) throw new Error('올바른 콘팩 ID가 아닙니다.');
  return request(`/v1/admin/sticker-packs/${encodeURIComponent(packId)}/settings`, {
    method: 'POST',
    body: updates,
  });
}

// surface 를 주면 그 채팅에서 보일 콘만 바꾼다. 다른 채팅의 설정은 건드리지 않는다.
export function saveActiveStickerIds(packId, stickerIds, surface = '') {
  if (!PACK_ID.test(packId)) throw new Error('올바른 콘팩 ID가 아닙니다.');
  return request(`/v1/admin/sticker-packs/${encodeURIComponent(packId)}/active-set`, {
    method: 'POST',
    body: surface ? { stickerIds, surface } : { stickerIds },
  });
}

export const STICKER_IMPORT_BATCH_SIZE = IMPORT_BATCH_SIZE;
